/**
 * Scope-tree browser + reverse lookup (Wave 5 of the authz rebuild).
 *
 * Renders the tenant's scope hierarchy root-down (grants point into this
 * structure — until now no screen showed it), and clicking a node answers
 * the question admins actually ask: "who has access to THIS?" — every
 * user/group assignment on the node. Assignments on ANCESTOR nodes also
 * cover a node through the resolver's ancestry walk; the panel says so
 * rather than pretending the node-local list is the whole story.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Network } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ResolvedId, useAdminNames } from '../nameResolution';
import { isScheduledAssignment, useAssignmentsForScopeNode, useScopeTree } from './hooks';
function buildOrder(nodes) {
    const children = new Map();
    const byId = new Map(nodes.map((n)=>[
            n.id,
            n
        ]));
    for (const n of nodes){
        const key = n.parentId && byId.has(n.parentId) ? n.parentId : '';
        children.set(key, [
            ...children.get(key) ?? [],
            n
        ]);
    }
    const out = [];
    const walk = (parent, depth)=>{
        for (const n of children.get(parent) ?? []){
            out.push({
                node: n,
                depth
            });
            walk(n.id, depth + 1);
        }
    };
    walk('', 0);
    return out;
}
export function ScopeTreePanel() {
    const { data: nodes, isLoading, error } = useScopeTree();
    const [selected, setSelected] = useState('');
    const { data: assignments, isLoading: loadingAssignments } = useAssignmentsForScopeNode(selected);
    const names = useAdminNames((assignments ?? []).map((a)=>a.userId));
    const rows = useMemo(()=>buildOrder((nodes ?? []).map((n)=>({
                id: n.id,
                parentId: n.parentId,
                kind: n.kind,
                displayName: n.displayName,
                sensitivity: n.sensitivity
            }))), [
        nodes
    ]);
    return /*#__PURE__*/ _jsxs(Card, {
        "data-testid": Testids.adminAccess.treePanel,
        children: [
            /*#__PURE__*/ _jsxs(CardHeader, {
                children: [
                    /*#__PURE__*/ _jsxs(CardTitle, {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Network, {
                                className: "h-4 w-4"
                            }),
                            " Scope tree"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CardDescription, {
                        children: "The hierarchy grants point into. Click a node to see who has access to it — assignments on ancestor nodes (up to the tenant root) also cover it via inheritance."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                className: "space-y-4",
                children: [
                    isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "Loading…"
                    }),
                    error && /*#__PURE__*/ _jsx("p", {
                        className: "text-destructive text-sm",
                        children: error instanceof Error ? error.message : 'Failed to load the scope tree'
                    }),
                    nodes && nodes.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "No scope nodes yet — the tree is minted as tenants, programs, and projects are created."
                    }),
                    rows.length > 0 && /*#__PURE__*/ _jsx("div", {
                        className: "max-h-72 overflow-auto rounded-md border",
                        children: rows.map(({ node, depth })=>/*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                "data-testid": Testids.adminAccess.treeNode,
                                "data-node-id": node.id,
                                className: `flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted ${selected === node.id ? 'bg-muted' : ''}`,
                                style: {
                                    paddingLeft: `${12 + depth * 20}px`
                                },
                                onClick: ()=>setSelected(node.id),
                                children: [
                                    /*#__PURE__*/ _jsx(Badge, {
                                        variant: node.kind === 'tenant' ? 'default' : 'secondary',
                                        children: node.kind
                                    }),
                                    /*#__PURE__*/ _jsx(ResolvedId, {
                                        id: node.id,
                                        name: node.displayName,
                                        className: "truncate"
                                    }),
                                    node.sensitivity && node.sensitivity !== 'standard' && /*#__PURE__*/ _jsx(Badge, {
                                        variant: "destructive",
                                        children: node.sensitivity
                                    })
                                ]
                            }, node.id))
                    }),
                    selected && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx("h4", {
                                className: "text-sm font-semibold",
                                children: "Who has access here"
                            }),
                            loadingAssignments && /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground text-sm",
                                children: "Loading…"
                            }),
                            assignments && assignments.length === 0 && /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground text-sm",
                                children: "No direct assignments on this node — access, if any, comes from ancestor nodes."
                            }),
                            assignments && assignments.length > 0 && /*#__PURE__*/ _jsxs(Table, {
                                children: [
                                    /*#__PURE__*/ _jsx(TableHeader, {
                                        children: /*#__PURE__*/ _jsxs(TableRow, {
                                            children: [
                                                /*#__PURE__*/ _jsx(TableHead, {
                                                    children: "Principal"
                                                }),
                                                /*#__PURE__*/ _jsx(TableHead, {
                                                    children: "Bundle"
                                                }),
                                                /*#__PURE__*/ _jsx(TableHead, {
                                                    children: "Type"
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TableBody, {
                                        children: assignments.map((a)=>/*#__PURE__*/ _jsxs(TableRow, {
                                                "data-testid": Testids.adminAccess.reverseRow,
                                                children: [
                                                    /*#__PURE__*/ _jsx(TableCell, {
                                                        className: "text-xs",
                                                        children: a.userId ? /*#__PURE__*/ _jsx(ResolvedId, {
                                                            id: a.userId,
                                                            name: names.user(a.userId)
                                                        }) : /*#__PURE__*/ _jsxs("span", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Badge, {
                                                                    variant: "outline",
                                                                    className: "mr-1",
                                                                    children: "group"
                                                                }),
                                                                /*#__PURE__*/ _jsx(ResolvedId, {
                                                                    id: a.groupId,
                                                                    name: names.group(a.groupId)
                                                                })
                                                            ]
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(TableCell, {
                                                        className: "text-xs",
                                                        children: /*#__PURE__*/ _jsx(ResolvedId, {
                                                            id: a.roleBundleKey,
                                                            name: names.bundle(a.roleBundleKey)
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsxs(TableCell, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Badge, {
                                                                variant: a.assignmentType === 'break_glass' ? 'destructive' : 'secondary',
                                                                children: a.assignmentType
                                                            }),
                                                            isScheduledAssignment(a) && /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "secondary",
                                                                className: "ml-1.5",
                                                                "data-testid": Testids.adminAccess.reverseScheduledBadge,
                                                                children: "Scheduled"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }, a.id))
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
